import React, { Component } from 'react';
import copyToClipboard from 'copy-to-clipboard';

import {
  SR_API_ACTION_UTILITY_PARTNER_CREATE_API_KEY,
  SR_API_ACTION_UTILITY_PARTNER_GET_API_KEYS,
  SR_API_ACTION_UTILITY_PARTNER_REVOKE_API_KEY,
  SR_API_ACTION_UTILITY_PARTNER_ROTATE_API_TOKEN,
} from 'api/utilityActionTypes';
import { SR_API_ERROR_UTILITY_REAUTHENTICATION_REQUIRED } from 'common/errorCodes';

import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrButton, {
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_BLUE_BORDER,
  SR_BUTTON_THEME_ADOBE_RED_OUTLINE,
} from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';

import './SrUtilityPartnerApiKeys.less';

const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
  year: 'numeric', month: 'short', day: 'numeric',
});

export default class SrUtilityPartnerApiKeys extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      loading: true,
      error: '',
      reauthenticate: false,
      form: null,
      name: '',
      scope: 'readOnly',
      confirmation: '',
      submitting: false,
      newToken: null,
      copied: false,
      feedback: '',
    };
  }

  componentDidMount() {
    this._mounted = true;
    this._load();
  }

  componentWillUnmount() {
    this._mounted = false;
    this.state.newToken = null;
  }

  _request = (action, payload = {}) => {
    return this.props.utilityApiClient.post(action, {
      utilityPartnerId: this.props.utilityPartner.id,
      ...payload,
    }, { omitResponseLog: true });
  };

  _handleError = (error) => {
    if (!this._mounted) return;
    if (error.statusCode === 403) {
      this.props.onAccessDenied();
      return;
    }
    this.setState({
      error: error.message,
      reauthenticate: error.name === SR_API_ERROR_UTILITY_REAUTHENTICATION_REQUIRED,
    });
  };

  _load = async () => {
    this.setState({ loading: true });
    try {
      const data = await this._request(SR_API_ACTION_UTILITY_PARTNER_GET_API_KEYS);
      if (this._mounted) this.setState({ data, error: '', reauthenticate: false });
    } catch (error) {
      this._handleError(error);
    } finally {
      if (this._mounted) this.setState({ loading: false });
    }
  };

  _openForm = (form) => {
    this.setState({
      form,
      name: '',
      scope: 'readOnly',
      confirmation: '',
      error: '',
      newToken: null,
      copied: false,
      feedback: '',
    }, () => {
      const formElement = this._formElement;
      if (formElement) {
        formElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        const input = formElement.querySelector('input, select, button');
        if (input) input.focus();
      }
    });
  };

  _submit = async (event) => {
    event.preventDefault();
    const { form, name, scope, confirmation, submitting } = this.state;
    if (submitting || !form || (form.type === 'rotate' && confirmation !== 'REGENERATE')) return;
    this.setState({ submitting: true, error: '', feedback: '' });
    try {
      let response;
      if (form.type === 'create') {
        response = await this._request(SR_API_ACTION_UTILITY_PARTNER_CREATE_API_KEY, {
          name: name.trim(), scope,
        });
      } else if (form.type === 'rotate') {
        response = await this._request(SR_API_ACTION_UTILITY_PARTNER_ROTATE_API_TOKEN, {
          confirmation,
        });
      } else {
        await this._request(SR_API_ACTION_UTILITY_PARTNER_REVOKE_API_KEY, { keyId: form.key.id });
      }
      if (!this._mounted) return;
      this.setState({
        form: null,
        newToken: response?.token || null,
        copied: false,
        feedback: form.type === 'revoke' ? 'API key revoked.' : '',
      }, () => {
        if (this._newTokenElement) {
          this._newTokenElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          this._newTokenElement.focus();
        }
      });
      await this._load();
    } catch (error) {
      this._handleError(error);
    } finally {
      if (this._mounted) this.setState({ submitting: false });
    }
  };

  _copy = () => {
    if (!this.state.newToken) return;
    const copied = copyToClipboard(this.state.newToken);
    this.setState({ copied, feedback: copied ? '' : 'Copy failed. Select and copy the key manually.' });
  };

  _renderButton = (label, onClick, { danger = false, disabled = false } = {}) => (
    <SrButton
      heightType={SR_BUTTON_HEIGHT_SHORT}
      minWidth="auto"
      theme={danger ? SR_BUTTON_THEME_ADOBE_RED_OUTLINE : SR_BUTTON_THEME_ADOBE_BLUE_BORDER}
      disabled={this.state.submitting || disabled}
      onClick={onClick}
    >
      {label}
    </SrButton>
  );

  _renderForm() {
    const { form, name, scope, confirmation, submitting } = this.state;
    if (!form) return null;
    const isCreate = form.type === 'create';
    const isRotate = form.type === 'rotate';
    const title = isCreate ? 'Create an API key' : isRotate ? 'Regenerate primary key?' : `Revoke ${form.key.name}?`;
    return (
      <form ref={(element) => { this._formElement = element; }} onSubmit={this._submit} styleName="actionForm" aria-labelledby="api-key-form-title">
        <h2 id="api-key-form-title">{title}</h2>
        {isCreate ? (
          <>
            <label htmlFor="api-key-name">Key name
            <input
              id="api-key-name"
              className="sr-input"
              value={name}
              maxLength={100}
              required
              disabled={submitting}
              onChange={(event) => this.setState({ name: event.target.value })}
            />
            </label>
            <label htmlFor="api-key-scope">Access
            <select
              id="api-key-scope"
              value={scope}
              disabled={submitting}
              onChange={(event) => this.setState({ scope: event.target.value })}
            >
              <option value="readOnly">Read-only</option>
              <option value="full">Full access</option>
            </select>
            </label>
            <p>Choose read-only unless this integration needs to change data.</p>
          </>
        ) : (
          <p>
            {isRotate
              ? 'Your current primary key will stop working immediately. Update every integration using it with the new key.'
              : 'This key will stop working immediately. Any integrations using it will lose access.'}
          </p>
        )}
        {isRotate && (
          <>
            <label htmlFor="api-key-confirmation">Type REGENERATE to confirm
            <input
              id="api-key-confirmation"
              className="sr-input"
              value={confirmation}
              autoComplete="off"
              disabled={submitting}
              onChange={(event) => this.setState({ confirmation: event.target.value })}
            />
            </label>
          </>
        )}
        <div styleName="actions">
          {this._renderButton('Cancel', () => this.setState({ form: null, error: '' }))}
          <SrButton
            type="submit"
            heightType={SR_BUTTON_HEIGHT_SHORT}
            minWidth="auto"
            theme={isCreate ? SR_BUTTON_THEME_ADOBE_BLUE : SR_BUTTON_THEME_ADOBE_RED_OUTLINE}
            disabled={submitting || (isCreate && !name.trim()) || (isRotate && confirmation !== 'REGENERATE')}
          >
            {submitting ? 'Saving…' : isCreate ? 'Create key' : isRotate ? 'Regenerate key' : 'Revoke key'}
          </SrButton>
        </div>
      </form>
    );
  }

  render() {
    const { utilityPartner } = this.props;
    const { data, loading, error, reauthenticate, newToken, copied, feedback, form } = this.state;
    const canCreate = data?.selfServeEnabled && data.keys.length < data.additionalKeyLimit;
    const returnPath = `/utility-profit/${utilityPartner.slug}/settings/api-keys`;
    return (
      <div styleName="apiKeys">
        <SrCard header="API keys" color={SR_CARD_BULLET_COLOR_BLUE}>
          <p>Use API keys to connect your systems to UtilityProfit. Keep them private.</p>
          {error && <p role="alert" styleName="error">{error}</p>}
          {reauthenticate && (
            <SrLink to={`/utility-profit/login?reauth=1&next=${encodeURIComponent(returnPath)}`}>
              Sign in again
            </SrLink>
          )}
          {newToken && (
            <section styleName="newKey" aria-labelledby="new-api-key-title" data-dd-privacy="mask">
              <h2 id="new-api-key-title">Save your new API key</h2>
              <p>Copy it now. You won't be able to see this key again.</p>
              <textarea ref={(element) => { this._newTokenElement = element; }} aria-label="New API key" readOnly value={newToken} spellCheck={false} />
              <div styleName="actions">
                {this._renderButton(copied ? 'Copied!' : 'Copy key', this._copy)}
                {this._renderButton('Done', () => this.setState({ newToken: null, copied: false }))}
              </div>
              {copied && <p role="status">API key copied.</p>}
            </section>
          )}
          {feedback && <p role="status">{feedback}</p>}
          {loading && <p role="status">Loading API keys…</p>}
          {!loading && !data && !reauthenticate && this._renderButton('Try again', this._load)}
          {data && !reauthenticate && (
            <>
              <section styleName="keySection" aria-labelledby="primary-key-title">
                <h2 id="primary-key-title">Primary key</h2>
                {data.primaryKey ? (
                  <div styleName="keyRow">
                    <div>
                      <code>{data.primaryKey.maskedToken}</code>
                      <p>Full access · Created {formatDate(data.primaryKey.createdDate)}</p>
                      {data.primaryKey.createdBy && <p>Created by {data.primaryKey.createdBy.name}</p>}
                    </div>
                    {this._renderButton('Regenerate', () => this._openForm({ type: 'rotate' }), { danger: true })}
                  </div>
                ) : <p>Your account team can help you set up a primary API key.</p>}
              </section>
              <section styleName="keySection" aria-labelledby="additional-keys-title">
                <div styleName="sectionHeader">
                  <h2 id="additional-keys-title">Additional keys</h2>
                  {data.selfServeEnabled && this._renderButton('Create API key', () => this._openForm({ type: 'create' }), { disabled: !canCreate })}
                </div>
                {!data.selfServeEnabled && <p>Contact your account team to enable creating additional keys.</p>}
                {data.selfServeEnabled && !canCreate && <p>You can have up to {data.additionalKeyLimit} additional keys. Revoke one to create another.</p>}
                {data.keys.length === 0 && <p>No additional API keys yet.</p>}
                {data.keys.map((key) => (
                  <div key={key.id} styleName="keyRow">
                    <div>
                      <h3>{key.name}</h3>
                      <code>{key.maskedToken}</code>
                      <p>{key.scope === 'readOnly' ? 'Read-only' : 'Full access'} · Created {formatDate(key.createdDate)}</p>
                      {key.createdBy && <p>Created by {key.createdBy.name}</p>}
                    </div>
                    {this._renderButton(`Revoke ${key.name}`, () => this._openForm({ type: 'revoke', key }), { danger: true })}
                  </div>
                ))}
              </section>
              {form && this._renderForm()}
            </>
          )}
        </SrCard>
      </div>
    );
  }
}

SrUtilityPartnerApiKeys.displayName = 'SrUtilityPartnerApiKeys';
